import { FlashList } from '@shopify/flash-list';
import { Image } from 'expo-image';
import { router, useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useSubstituableCars } from '@/api/partners/cars/use-substituable-cars';
import { useChangeCar } from '@/api/partners/reservations/use-change-car';
import { useReservation } from '@/api/partners/reservations/use-reservation';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { getCarBrandLogo, getCarImages } from '@/core';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import { Icon } from '@/ui/icon';
import { Search } from '@/ui/icons';
import { Loader } from '@/ui/loader';

export default function ChangeCar() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const [text, setText] = useState('');
  const [activeCar, setActiveCar] = useState<Car | null>(null);
  const debouncedText = useDebounce(text, 400);
  const [showCarChangeModal, setShowCarChangeModal] = useState(false);

  const {
    data: reservationData,
    refetch: refetchReservation,
    isLoading: isLoadingReservation,
  } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  const {
    data: substitutableCarsData,
    isFetchingNextPage,
    fetchNextPage,
    isLoading: isLoadingSubstitutableCars,
    hasNextPage,
    refetch: refetchSubstitutableCars,
  } = useSubstituableCars({
    tripId: reservationData?.id,
    startTime: reservationData?.startTime,
    endTime: reservationData?.endTime,
    text: debouncedText,
  });

  const { mutateAsync: changeCar, isPending: isChangingCar } = useChangeCar();

  const substituableCars = useMemo(() => {
    return substitutableCarsData?.pages.map((page) => page.data).flat();
  }, [substitutableCarsData]);

  const coverPhoto = useMemo(() => {
    if (!reservationData) return null;

    if (
      !reservationData.car.images ||
      Object.keys(reservationData.car.images).length === 0
    )
      return null;

    return getCarImages(reservationData.car.images)[0].url;
  }, [reservationData]);

  const handleCarChange = async () => {
    try {
      await changeCar({
        id: reservationData!.id,
        carId: activeCar!.id,
      });

      refetchReservation();
      setActiveCar(null);

      Toast.show({
        text1: 'Car changed successfully!',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  if (isLoadingReservation || isChangingCar) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <>
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          paddingTop: 16,
        }}
      >
        <View style={styles.container}>
          <View style={{ padding: 16, gap: 8 }}>
            <View style={styles.nav}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.navigate(
                    `/partner/reservations/${reservationData.id}`
                  );
                }}
              />

              <Text type="title1">Change Car</Text>
            </View>
          </View>

          <View style={styles.body}>
            <View style={{ padding: 16, gap: 16 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Current car
              </Text>

              <Card>
                <View
                  style={{
                    padding: 16,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <View
                    style={{
                      flexShrink: 1,
                      flex: 1,
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: theme.colors.icon.fill,
                        padding: 10,
                        borderRadius: 8,
                        width: 40,
                        height: 40,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Image
                        style={{
                          width: 26,
                          height: 26,
                        }}
                        source={getCarBrandLogo(reservationData.car.brandLogos)}
                        contentFit="cover"
                      />
                    </View>

                    <View style={{ flexShrink: 1, flexDirection: 'column' }}>
                      <View>
                        <Text type="bodyBold">
                          {reservationData.car.brandName}{' '}
                          {reservationData.car.modelName} -{' '}
                          {reservationData.car.licensePlate}
                        </Text>
                      </View>

                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {reservationData.car.defaultLocation.address}
                        </Text>
                      </View>
                    </View>
                  </View>

                  <Image
                    style={{
                      width: 100,
                    }}
                    source={coverPhoto}
                    contentFit="cover"
                  />
                </View>
              </Card>
            </View>

            <View style={styles.chooseNewCarContainer}>
              <Text type="bodyRegular">Choose a new car</Text>

              <View style={{ flex: 1, zIndex: 1 }}>
                <Input
                  placeholder="Search cars"
                  value={text}
                  onChangeText={(t: string) => setText(t)}
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<Search />}
                      fillColor={theme.colors.icon.color}
                      width={22}
                      height={22}
                    />
                  }
                  containerStyle={{
                    maxHeight: 75,
                  }}
                />

                <FlashList
                  data={substituableCars}
                  extraData={activeCar}
                  renderItem={({ item }) => (
                    <CheckItem
                      text={`${item.brandName} ${item.modelName} - ${item.licensePlate}`}
                      caption={`${item.defaultLocation.address}`}
                      active={activeCar?.id === item.id}
                      onPress={() => {
                        if (activeCar?.id === item.id) {
                          setActiveCar(null);
                          return;
                        } else {
                          setActiveCar(item);
                        }
                      }}
                      type="checkbox"
                    />
                  )}
                  ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
                  onEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      console.log('end reached');
                      fetchNextPage();
                    }
                  }}
                  estimatedItemSize={5}
                  contentContainerStyle={styles.flashContentContainer}
                  showsVerticalScrollIndicator={false}
                  ListFooterComponent={() => {
                    if (!isFetchingNextPage) return null;

                    return <Loader style={{ height: 140 }} />;
                  }}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <Button
                text="Change Car"
                type="primary"
                onPress={() => {
                  if (
                    activeCar?.defaultLocation.address ===
                    reservationData?.pickupLocation.address
                  ) {
                    handleCarChange();
                  } else {
                    setShowCarChangeModal(true);
                  }
                }}
                disabled={!activeCar}
              />
            </View>
          </View>
        </View>
      </View>

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={1000}
        isVisible={showCarChangeModal}
        style={{
          ...styles.changeCarContainer,
        }}
        hasBackdrop
        onBackdropPress={() => {
          setShowCarChangeModal(false);
        }}
      >
        <View
          style={{
            ...styles.changeCarContainerView,
          }}
        >
          <View
            style={{
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <Text type="title2">Confirm Car Change</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowCarChangeModal(false);
              }}
              style={{
                position: 'absolute',
                right: 0,
              }}
            />
          </View>

          <View style={{ gap: 16 }}>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              You're switching to:
            </Text>

            <Text type="bodyRegular">
              {activeCar?.brandName} {activeCar?.modelName} -{' '}
              {activeCar?.licensePlate} at{' '}
              <Text style={{ textDecorationLine: 'underline' }}>
                {activeCar?.defaultLocation.address}
              </Text>
            </Text>

            <Text type="bodyRegular">
              and will deliver it to the original pickup location to{' '}
              <Text style={{ textDecorationLine: 'underline' }}>
                {reservationData.pickupLocation.address}
              </Text>
            </Text>

            <Text type="bodyRegular">
              You will be responsible for transporting this car to the original
              pickup location.
            </Text>
          </View>

          <View style={{ flexGrow: 1, width: '100%', gap: 8 }}>
            <Button
              text="Confirm"
              type="primary"
              onPress={() => {
                setShowCarChangeModal(false);
                handleCarChange();
              }}
            />

            <Button
              text="Cancel"
              type="ghost"
              onPress={() => {
                setShowCarChangeModal(false);
              }}
            />
          </View>
        </View>
      </Modal>
    </>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  container: {
    flexGrow: {
      xs: 1,
      md: undefined,
    },
    flex: {
      md: 1,
    },
    paddingLeft: {
      md: 56,
    },
  },
  body: {
    flex: 1,
    width: {
      md: 700,
    },
  },
  flashContentContainer: {
    paddingBottom: {
      xs: 100,
      md: 0,
    },
  },
  chooseNewCarContainer: {
    flex: 1,
    padding: 16,
    gap: 16,
  },
  buttonContainer: {
    padding: 16,
    width: {
      xs: 'auto',
      md: 400,
    },
  },
  changeCarContainer: {
    margin: 0,
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 700,
    },
    height: 300,
  },
  changeCarContainerView: {
    padding: 16,
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
}));
